/**
 * Aspect Ratio Conversion Service
 * 
 * Business logic for aspect ratio conversion between 16:9 and 9:16
 * Handles calculations, validations, and transformations
 */

import {
  MediaItem,
  MediaAdjustments,
  MediaPosition,
  MediaScale,
  CropBounds,
  ResolutionInfo,
  DEFAULT_ADJUSTMENTS,
  ADJUSTMENT_LIMITS,
  SUPPORTED_CONVERSIONS
} from '../../types';

export class AspectRatioConversionService {
  /**
   * Get default adjustments for a media item
   */
  static getDefaultAdjustments(): MediaAdjustments {
    return { ...DEFAULT_ADJUSTMENTS };
  }

  /**
   * Apply adjustments to a media item (immutable)
   */
  static applyAdjustmentToMediaItem(
    item: MediaItem,
    adjustment: Partial<MediaAdjustments>
  ): MediaItem {
    return {
      ...item,
      position: adjustment.position || item.position,
      scale: adjustment.scale || item.scale
    };
  }

  /**
   * Clamp position values to valid range
   */
  static clampPosition(x: number, y: number): MediaPosition {
    return {
      x: Math.max(
        ADJUSTMENT_LIMITS.position.min,
        Math.min(ADJUSTMENT_LIMITS.position.max, x)
      ),
      y: Math.max(
        ADJUSTMENT_LIMITS.position.min,
        Math.min(ADJUSTMENT_LIMITS.position.max, y)
      )
    };
  }

  /**
   * Clamp scale value to valid range
   */
  static clampScale(scale: number): number {
    return Math.max(
      ADJUSTMENT_LIMITS.scale.min,
      Math.min(ADJUSTMENT_LIMITS.scale.max, scale)
    );
  }

  /**
   * Clamp full scale object
   */
  static clampScaleObject(scale: MediaScale): MediaScale {
    return {
      scaleX: this.clampScale(scale.scaleX),
      scaleY: this.clampScale(scale.scaleY)
    };
  }

  /**
   * Validate if adjustments are within acceptable bounds
   */
  static validateAdjustments(adjustments: MediaAdjustments): boolean {
    if (adjustments.position) {
      const { x, y } = adjustments.position;
      if (
        x < ADJUSTMENT_LIMITS.position.min ||
        x > ADJUSTMENT_LIMITS.position.max ||
        y < ADJUSTMENT_LIMITS.position.min ||
        y > ADJUSTMENT_LIMITS.position.max
      ) {
        return false;
      }
    }

    if (adjustments.scale) {
      const { scaleX, scaleY } = adjustments.scale;
      if (
        scaleX < ADJUSTMENT_LIMITS.scale.min ||
        scaleX > ADJUSTMENT_LIMITS.scale.max ||
        scaleY < ADJUSTMENT_LIMITS.scale.min ||
        scaleY > ADJUSTMENT_LIMITS.scale.max
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Calculate crop bounds for aspect ratio conversion
   */
  static calculateCropBounds(
    sourceAspectRatio: string,
    targetAspectRatio: string,
    sourceWidth: number,
    sourceHeight: number,
    adjustments?: MediaAdjustments
  ): CropBounds {
    // Parse aspect ratios
    const [sourceW, sourceH] = sourceAspectRatio.split(':').map(Number);
    const [targetW, targetH] = targetAspectRatio.split(':').map(Number);

    const sourceRatio = sourceW / sourceH;
    const targetRatio = targetW / targetH;

    // Calculate base crop dimensions
    let cropWidth: number;
    let cropHeight: number;

    if (targetRatio > sourceRatio) {
      // Target is wider - crop height
      cropWidth = sourceWidth;
      cropHeight = sourceWidth / targetRatio;
    } else {
      // Target is taller - crop width
      cropHeight = sourceHeight;
      cropWidth = sourceHeight * targetRatio;
    }

    // Apply scale adjustments
    const scale = adjustments?.scale || { scaleX: 1, scaleY: 1 };
    cropWidth = cropWidth / scale.scaleX;
    cropHeight = cropHeight / scale.scaleY;

    // Calculate centered position
    let cropX = (sourceWidth - cropWidth) / 2;
    let cropY = (sourceHeight - cropHeight) / 2;

    // Apply position adjustments (percentage to pixels)
    if (adjustments?.position) {
      const { x, y } = adjustments.position;
      cropX += (x / 100) * sourceWidth;
      cropY += (y / 100) * sourceHeight;
    }

    // Ensure crop stays within bounds
    cropX = Math.max(0, Math.min(sourceWidth - cropWidth, cropX));
    cropY = Math.max(0, Math.min(sourceHeight - cropHeight, cropY));

    return {
      x: Math.round(cropX),
      y: Math.round(cropY),
      width: Math.round(cropWidth),
      height: Math.round(cropHeight)
    };
  }

  /**
   * Check if aspect ratio conversion is supported
   */
  static isConversionSupported(
    sourceRatio: string,
    targetRatio: string
  ): boolean {
    const supported = SUPPORTED_CONVERSIONS[sourceRatio as keyof typeof SUPPORTED_CONVERSIONS];
    return supported ? (supported as readonly string[]).includes(targetRatio) : false;
  }

  /**
   * Reset adjustments for a media item
   */
  static resetMediaItemAdjustments(item: MediaItem): MediaItem {
    const { position, scale, ...rest } = item;
    return rest;
  }

  /**
   * Reset adjustments for all media items
   */
  static resetAllAdjustments(items: MediaItem[]): MediaItem[] {
    return items.map(item => this.resetMediaItemAdjustments(item));
  }

  /**
   * Initialize all media items with default adjustments
   */
  static initializeMediaItemsWithDefaults(items: MediaItem[]): MediaItem[] {
    return items.map(item => ({
      ...item,
      position: item.position || { x: 0, y: 0 },
      scale: item.scale || { scaleX: 1, scaleY: 1 }
    }));
  }

  /**
   * Update position for a specific media item
   */
  static updateMediaItemPosition(
    items: MediaItem[],
    itemId: string,
    position: Partial<MediaPosition>
  ): MediaItem[] {
    return items.map(item => {
      if (item.id === itemId) {
        const currentPosition = item.position || { x: 0, y: 0 };
        const newPosition = this.clampPosition(
          position.x !== undefined ? position.x : currentPosition.x,
          position.y !== undefined ? position.y : currentPosition.y
        );
        return { ...item, position: newPosition };
      }
      return item;
    });
  }

  /**
   * Update scale for a specific media item
   */
  static updateMediaItemScale(
    items: MediaItem[],
    itemId: string,
    scale: Partial<MediaScale>
  ): MediaItem[] {
    return items.map(item => {
      if (item.id === itemId) {
        const currentScale = item.scale || { scaleX: 1, scaleY: 1 };
        const newScale = this.clampScaleObject({
          scaleX: scale.scaleX !== undefined ? scale.scaleX : currentScale.scaleX,
          scaleY: scale.scaleY !== undefined ? scale.scaleY : currentScale.scaleY
        });
        return { ...item, scale: newScale };
      }
      return item;
    });
  }

  /**
   * Nudge position by delta values
   */
  static nudgePosition(
    currentPosition: MediaPosition,
    deltaX: number,
    deltaY: number
  ): MediaPosition {
    return this.clampPosition(
      currentPosition.x + deltaX,
      currentPosition.y + deltaY
    );
  }

  /**
   * Get resolution info for conversion
   */
  static getResolutionInfo(
    sourceRatio: string,
    targetRatio: string
  ): ResolutionInfo | null {
    // Common resolutions map
    const resolutions: Record<string, { width: number; height: number }> = {
      '16:9': { width: 1920, height: 1080 },
      '9:16': { width: 1080, height: 1920 },
      '1:1': { width: 1080, height: 1080 }
    };

    const source = resolutions[sourceRatio];
    const target = resolutions[targetRatio];

    if (!source || !target) {
      return null;
    }

    return {
      source: { ...source, aspectRatio: sourceRatio },
      target: { ...target, aspectRatio: targetRatio }
    };
  }
}
